import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchNewsById } from "../services/newsPublic";
import Header from "../components/header";
import { useSectionNavigation } from "../components/contexts/SectionContext";
import Footer from "../components/footer";

const NewsSlider = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  useEffect(() => {
    if (images.length <= 1) return;

    const interval = setInterval(goToNext, 5000);
    return () => clearInterval(interval);
  }, [goToNext, images.length]);

  if (images.length === 0) return null;

  return (
    <div className="news-slider">
      {images.length > 1 && (
        <>
          <button className="slider-arrow left" onClick={goToPrev}>
            &#10094;
          </button>
          <button className="slider-arrow right" onClick={goToNext}>
            &#10095;
          </button>
        </>
      )}

      <div className="slider-images-container">
        {images.map((image, index) => (
          <div key={index} className="slider-item">
            <img
              src={image}
              alt=""
              className={`slider-image ${index === currentIndex ? "active" : ""}`}
              style={{ opacity: index === currentIndex ? 1 : 0 }}
              loading="eager"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default function News() {
  const { scrollToSection } = useSectionNavigation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [newsItem, setNewsItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);

    fetchNewsById(id)
      .then(setNewsItem)
      .catch(() => {
        setNewsItem(null);
        setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "instant",
      });
    }, 0);
  }, [id]);

  const handleClick = (sectionId, e) => {
    e.preventDefault();
    scrollToSection(sectionId);
  };

  if (loading) {
    return (
      <div className="news-page">
        <Header />
        <div className="newsContent">
          <p>Загрузка...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !newsItem) {
    return (
      <div className="news-page">
        <Header />
        <div className="newsContent">
          <h1>Новость не найдена</h1>
          <p>Запрошенная новость не существует или была удалена.</p>
          <div className="newsLink_bottom">
            <Link to="/all-news" className="backLink">
              Все новости
            </Link>
            <Link to="/" className="backLink">
              Вернуться на главную
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const images = newsItem.images?.length
    ? newsItem.images
    : newsItem.imagePreview
      ? [newsItem.imagePreview]
      : [];

  return (
    <div className="news-page">
      <Header />
      <div className="container">
        <div className="newsContent">
          <div className="newsContent_header">
            <h1 className="newsTitle">Пресс-центр</h1>
            <h4 className="newsTitle_bottom">
              {newsItem.title}
              {newsItem.link && (
                <a
                  href={newsItem.link}
                  target="_blank"
                  rel="noreferrer"
                  className="targetLink"
                >
                  Источник
                </a>
              )}
            </h4>
            <p className="news-meta">
              {newsItem.date} {newsItem.tegs}
            </p>
          </div>
          <div className="newsContent_body">
            <NewsSlider images={images} />
            <div dangerouslySetInnerHTML={{ __html: newsItem.text }} />
          </div>
        </div>
      </div>
      {newsItem.textBottom && (
        <div className="container">
          <div
            className="newsText_bottom"
            dangerouslySetInnerHTML={{ __html: newsItem.textBottom }}
          />
        </div>
      )}
      <div className="container">
        <div className="newsLink_bottom">
          <Link to="/all-news" className="backLink">
            Все новости
          </Link>
          <Link
            to="/"
            onClick={(e) => {
              e.preventDefault();
              handleClick("home", e);
              navigate("/");
            }}
            className="backLink"
          >
            Вернуться на главную
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
